import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { NAV_LINKS, scrollToId, PRIMARY_LOGO } from "./shared";

export const Nav = () => {
  const [active, setActive] = useState("home");
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const onHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!onHome) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: "-45% 0px -50% 0px", threshold: 0 }
    );
    NAV_LINKS.forEach((l) => {
      const el = document.getElementById(l.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [onHome]);

  const go = (l) => {
    setOpen(false);
    if (l.route) {
      navigate(l.route);
      return;
    }
    if (!onHome) {
      navigate("/", { state: { scrollTo: l.id } });
    } else {
      scrollToId(l.id);
    }
  };

  const goHome = () => {
    setOpen(false);
    if (!onHome) navigate("/", { state: { scrollTo: "home" } });
    else scrollToId("home");
  };

  const isActive = (l) => (l.route ? location.pathname === l.route : onHome && active === l.id);

  return (
    <header
      data-testid="site-nav"
      className="fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,box-shadow] duration-500"
      style={{
        backgroundColor: scrolled ? "rgba(250,249,247,0.82)" : "rgba(250,249,247,0)",
        backdropFilter: scrolled ? "blur(16px)" : "none",
        borderBottom: scrolled ? "1px solid #D8D8D8" : "1px solid transparent",
      }}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={goHome}
          className="flex items-center group"
          aria-label="Fortim Ventures home"
        >
          <img
            src={PRIMARY_LOGO}
            alt="Fortim Ventures Private Limited"
            className="h-8 sm:h-10 w-auto"
          />
        </button>

        <nav className="hidden xl:flex items-center gap-6">
          {NAV_LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l)}
              className="relative text-sm font-medium transition-colors duration-300"
              style={{ color: isActive(l) ? "#3B2C82" : "#2B2B2B" }}
            >
              {l.label}
              {isActive(l) && (
                <motion.span
                  layoutId="nav-indicator"
                  className="absolute -bottom-2 left-0 right-0 h-0.5"
                  style={{ background: "#F5821F" }}
                  transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                />
              )}
            </button>
          ))}
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((o) => !o)}
          className="xl:hidden p-2"
          style={{ color: "#3B2C82" }}
          aria-label="Menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="xl:hidden overflow-hidden"
            style={{ backgroundColor: "rgba(250,249,247,0.98)", borderBottom: "1px solid #D8D8D8" }}
          >
            <div className="px-6 py-4 flex flex-col">
              {NAV_LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`mobile-nav-link-${l.id}`}
                  onClick={() => go(l)}
                  className="py-3 text-left text-base font-medium border-b"
                  style={{ color: isActive(l) ? "#3B2C82" : "#2B2B2B", borderColor: "#F2F1EF" }}
                >
                  {l.label}
                </button>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
